import { isBrowser } from '@backyard-ui/utils';
import { datadogRum } from '@datadog/browser-rum';

import { HttpClient } from '@/application/interfaces/http-client';

import { logger } from '../logger';

const BASE_STACK_LEVELS_TO_REMOVE = 4;

interface Options {
  stackLevelsToRemove?: number;
}

type LoggerFn = (
  error: unknown,
  context?: Record<string, any>,
  options?: Options
) => void;

type IErrorReporter = Record<'report', LoggerFn>;

export const ErrorReporter: IErrorReporter = {
  report(error, context, options) {
    const finalError = buildError(error, context, options);

    if (isBrowser) {
      datadogRum.addError(finalError, context);
    } else {
      logger.error(finalError.message, {
        ...context,
        err: {
          name: finalError.name,
          stack: finalError.stack,
          cause: finalError.cause,
        },
      });
    }
  },
};

/**
 *  Remove levels from original stack because errors created
 *  inside error-reporter.ts will consider error-reporter.ts
 *  as high level of the stack
 */
const createSanitizedError = (props: {
  message: string;
  errorOptions?: ErrorOptions;
  options?: Options;
}) => {
  const { errorOptions, message, options } = props;
  const linesToRemove =
    BASE_STACK_LEVELS_TO_REMOVE + (options?.stackLevelsToRemove ?? 0);

  const error = new Error(message, errorOptions);
  const lines = (error.stack ?? '').split('\n');
  error.stack = lines.slice(linesToRemove, lines.length).join('\n');

  return error;
};

/**
 * In a try/catch, the type of error is unknown.
 * This function will build an Error object from the unknown error.
 */
const buildError = (
  maybeError: unknown,
  context?: Record<string, any>,
  options?: Options
) => {
  if (!maybeError) {
    return createSanitizedError({ message: 'Unknown error', options });
  }

  if (maybeError instanceof Error) {
    return maybeError;
  }

  if (typeof maybeError === 'string') {
    return createSanitizedError({ message: maybeError, options });
  }

  if (maybeError instanceof HttpClient.Response) {
    return createSanitizedError({
      message: `HTTP Error: ${maybeError.status} - ${
        context?.url ?? 'Unknown URL'
      }`,
      errorOptions: {
        cause: maybeError.body,
      },
      options,
    });
  }

  try {
    return createSanitizedError({
      message: JSON.stringify(maybeError),
      options,
    });
  } catch {
    const getErrorMessage = (error: unknown): string => {
      if (error instanceof Error) return error.message;
      if (typeof error === 'string') return error;
      try {
        return JSON.stringify(error);
      } catch {
        return String(error);
      }
    };

    return createSanitizedError({
      message: getErrorMessage(maybeError),
      options,
    });
  }
};
